/**
 * Signature DTOs — document-version signatures (Prisma `Signature`,
 * schema.prisma:647). Captured against a specific document version (RAMS
 * sign-off, etc.); carries the rendered signature image URL + audit
 * metadata.
 *
 * The per-document sign-request flow's DTOs (DocumentSignRequestSchema /
 * DocumentSignatureSchema / DocumentSignRequestCreate) were REMOVED in
 * #2226: the flow is retired (epic #1162 #1161 — both mobile route verbs
 * return 410 SIGN_REQUEST_RETIRED; workers sign via the project crew link),
 * and the DTOs' last runtime consumer went with the mobile client method in
 * #2227. The Prisma models and their historical rows remain — only the wire
 * contracts for the retired surface are gone.
 *
 * Signature.id is a Prisma uuid() default, validated with the shared
 * `entityIdSchema` (non-empty opaque string) rather than a format-specific
 * check, so demo/seed non-UUID ids parse too.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";

// ─── Document version signatures (Prisma Signature) ────────────────────

/**
 * RESPONSE schema — deliberately NOT `.strict()` (issue #2411). A mobile
 * build parses this with the schema it was compiled against, so a server that
 * adds a field would make an installed build FAIL to parse rather than ignore
 * the new key. The server-side over-serialisation guard is preserved by
 * applying `.strict()` at the response boundary instead. `SignRequest` below
 * keeps `.strict()` — it is a REQUEST schema the server validates. Enforced
 * by `__tests__/contracts/mobile-wire-strictness.test.ts`.
 */
export const SignatureSchema = z
  .object({
    id: entityIdSchema,
    documentVersionId: entityIdSchema,
    signerName: z.string(),
    signerRole: z.string(),
    signerCompany: z.string().nullable(),
    ipAddress: z.string(),
    userAgent: z.string(),
    signatureImageUrl: z.string(),
    signatureHash: z.string(),
    signedAt: z.string().datetime(),
    consentTimestamp: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
  });

export type Signature = z.infer<typeof SignatureSchema>;

/**
 * POST /api/mobile/v1/documents/:versionId/signatures body. Mirrors the
 * web `signatureSubmissionSchema` (lib/validators.ts) — `consentGiven` MUST
 * be true and `consentTimestamp` MUST be an ISO datetime, otherwise the
 * server returns VALIDATION_FAILED.
 */
export const SignRequest = z
  .object({
    signerName: z.string().min(1),
    signerRole: z.string().min(1),
    signerCompany: z.string().optional(),
    signatureImageBase64: z
      .string()
      .describe(
        "PNG data URL or raw base64 of the rendered signature. Server uploads to DO Spaces and persists the URL.",
      ),
    consentGiven: z.literal(true),
    consentTimestamp: z.string().datetime(),
  })
  .strict();

export type SignRequest = z.infer<typeof SignRequest>;

/** RESPONSE schema — deliberately NOT `.strict()`; see `SignatureSchema`
 * above and issue #2411. */
export const SignResponse = z
  .object({
    signature: SignatureSchema,
  });

export type SignResponse = z.infer<typeof SignResponse>;

